import { useCart } from "@/context/CartContext";
import { useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/PublicChrome";
import { Trash2, Plus, Minus, ClipboardList, CheckCircle2 } from "lucide-react";

const WhatsAppIcon = ({ size = 16, className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

/** Constrói o link wa.me a partir do número E.164 + texto (mensagem completa). */
function buildWaLink(whatsapp_to, message) {
    if (!whatsapp_to) return null;
    // wa.me exige número sem +, sem traços, sem espaços
    const cleaned = String(whatsapp_to).replace(/[^0-9]/g, "");
    if (!cleaned) return null;
    return `https://wa.me/${cleaned}?text=${encodeURIComponent(message || "")}`;
}

export default function ListaOrcamento() {
    const { items, remove, setQty, setNotes, clear } = useCart();
    const [form, setForm] = useState({ name: "", email: "", company: "", cnpj: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!items.length) return toast.error("Sua lista está vazia");

        // Pré-abre janela sincronamente no gesto do usuário para escapar do popup blocker.
        // Se o backend não retornar whatsapp_to, fechamos a janela silenciosamente depois.
        const waWindow = window.open("about:blank", "_blank");

        setSending(true);
        try {
            const { data } = await api.post("/quotes", {
                ...form,
                items: items.map((i) => ({
                    product_id: i.product_id, name: i.name, sku: i.sku,
                    brand_name: i.brand_name, quantity: i.quantity, notes: i.notes || null,
                })),
            });
            setDone(data);
            clear();
            toast.success("Orçamento enviado! Nosso vendedor entrará em contato.");

            // Redireciona a janela pré-aberta para o wa.me com a mensagem completa (Opção C automática)
            const waLink = buildWaLink(data.whatsapp_to, data.wa_message);
            if (waLink && waWindow) {
                try { waWindow.location.href = waLink; } catch { /* noop */ }
            } else if (waWindow) {
                waWindow.close();
            }
        } catch (err) {
            if (waWindow) try { waWindow.close(); } catch { /* noop */ }
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao enviar");
        } finally { setSending(false); }
    };

    if (done) {
        const waLink = buildWaLink(done.whatsapp_to, done.wa_message);
        return (
            <div className="min-h-screen">
                <PublicHeader />
                <div className="max-w-2xl mx-auto px-4 py-24 text-center">
                    <CheckCircle2 size={72} className="text-brand-blue mx-auto" />
                    <div className="mt-6 text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">recebido</div>
                    <h1 className="mt-2 font-heading font-black text-4xl tracking-tight">Orçamento enviado!</h1>
                    <p className="mt-4 text-neutral-600">Nosso vendedor recebeu sua lista e responderá pelo canal que preferir. Guarde o número:</p>
                    <div className="mt-6 font-mono text-sm bg-neutral-100 border border-neutral-300 p-4 inline-block">#{done.id.slice(0,8).toUpperCase()}</div>

                    {waLink && (
                        <div className="mt-10 border border-neutral-300 bg-white p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">// aviso ao vendedor</div>
                            <p className="text-sm text-neutral-600 mb-4">
                                Já abrimos uma conversa no seu WhatsApp para você confirmar o envio ao vendedor. Se não abriu automaticamente (bloqueio de popup), clique abaixo:
                            </p>
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="wa-fallback-btn"
                                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-6 h-12 font-heading font-bold uppercase tracking-wider text-sm"
                            >
                                <WhatsAppIcon size={16} /> Abrir WhatsApp
                            </a>
                        </div>
                    )}

                    <div className="mt-10">
                        <Link to="/produtos" className="inline-flex items-center gap-2 bg-brand-blue text-white px-6 h-12 font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover" data-testid="link-back-catalog">
                            Voltar ao catálogo
                        </Link>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <PublicHeader />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">Lista de orçamento</div>
                <h1 className="mt-2 font-heading font-black text-4xl tracking-tight">Sua lista ({items.length})</h1>
                <p className="mt-2 text-sm text-neutral-500">Sem valores exibidos. Nosso vendedor retorna com preços e disponibilidade pelo WhatsApp/e-mail informado.</p>

                {items.length === 0 ? (
                    <div className="mt-8 border border-dashed border-neutral-300 p-16 text-center bg-white">
                        <ClipboardList size={40} className="text-neutral-400 mx-auto" />
                        <p className="mt-4 text-neutral-600">Sua lista está vazia.</p>
                        <Link to="/produtos" data-testid="link-empty-catalog" className="mt-6 inline-block text-brand-blue underline">Ver catálogo →</Link>
                    </div>
                ) : (
                    <div className="mt-8 grid lg:grid-cols-[1fr,380px] gap-8">
                        <div className="space-y-3">
                            {items.map((it) => (
                                <div key={it.product_id} className="bg-white border border-neutral-300 p-4 grid grid-cols-[80px,1fr,auto] gap-4" data-testid={`lista-item-${it.product_id}`}>
                                    <div className="w-20 h-20 bg-neutral-100 border border-neutral-200 overflow-hidden">
                                        {it.image_url ? <img src={fileUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center font-mono text-[10px] text-neutral-400">n/a</div>}
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{it.brand_name} · {it.sku || "s/SKU"}</div>
                                        <div className="mt-1 font-heading font-bold">{it.name}</div>
                                        <textarea placeholder="Observação (opcional) — ex.: preciso na cor branca" value={it.notes || ""} onChange={(e) => setNotes(it.product_id, e.target.value)} className="mt-2 w-full text-xs font-mono border border-neutral-200 p-2 h-16" data-testid={`notes-${it.product_id}`} />
                                    </div>
                                    <div className="flex flex-col items-end justify-between">
                                        <button data-testid={`remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-red-600 hover:bg-red-50 p-1"><Trash2 size={14} /></button>
                                        <div className="flex items-center border border-neutral-300">
                                            <button data-testid={`minus-${it.product_id}`} onClick={() => setQty(it.product_id, it.quantity - 1)} className="w-8 h-8 grid place-items-center hover:bg-neutral-100"><Minus size={12} /></button>
                                            <div className="w-10 text-center font-mono text-sm">{it.quantity}</div>
                                            <button data-testid={`plus-${it.product_id}`} onClick={() => setQty(it.product_id, it.quantity + 1)} className="w-8 h-8 grid place-items-center hover:bg-neutral-100"><Plus size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <form onSubmit={submit} className="bg-white border border-neutral-300 p-5 h-fit sticky top-24" data-testid="orcamento-form">
                            <div className="text-xs font-mono uppercase tracking-widest text-brand-blue">Seus dados</div>
                            <h2 className="mt-1 font-heading font-bold text-xl">Solicitar orçamento</h2>
                            <div className="mt-4 space-y-3">
                                <Input label="Nome" required value={form.name} onChange={(v) => setForm({...form, name: v})} testId="q-name" />
                                <Input label="E-mail" type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})} testId="q-email" />
                                <Input label="Empresa" value={form.company} onChange={(v) => setForm({...form, company: v})} testId="q-company" />
                                <div className="grid grid-cols-2 gap-2">
                                    <Input label="CNPJ" value={form.cnpj} onChange={(v) => setForm({...form, cnpj: v})} testId="q-cnpj" />
                                    <Input label="Telefone" value={form.phone} onChange={(v) => setForm({...form, phone: v})} testId="q-phone" />
                                </div>
                                <label className="block">
                                    <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Mensagem (opcional)</span>
                                    <textarea rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="mt-1 w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-brand-blue text-sm" data-testid="q-message" />
                                </label>
                            </div>
                            <button disabled={sending} data-testid="q-submit" className="mt-5 w-full h-12 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-60">
                                {sending ? "enviando…" : `Enviar (${items.length} item${items.length>1?'s':''})`}
                            </button>
                            <p className="mt-3 text-xs text-neutral-500 font-mono">Ao enviar, seu WhatsApp abre automaticamente com a mensagem pronta para o nosso vendedor.</p>
                        </form>
                    </div>
                )}
            </div>
            <PublicFooter />
        </div>
    );
}

const Input = ({ label, value, onChange, type = "text", required, testId }) => (
    <label className="block">
        <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">{label}{required && " *"}</span>
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 w-full h-11 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue text-sm" />
    </label>
);
